import React, { useState, useEffect } from 'react';
import { X, TrendingUp, Award, AlertTriangle, CheckCircle, ChevronRight, ChevronDown, BarChart3, Target, Zap, Activity, Menu, RefreshCw, Printer } from 'lucide-react';
import './StatGenieReportCard.css';
import ScoreCard from './score-card';
import ModeSelectionModal from './ModeSelectionModal';
import ModeToggle from './ModeToggle';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * StatGenie Report Card - Statement-based AI insights with modal overlays
 * 
 * Design principles:
 * - Clean, modern, professional
 * - Generous whitespace
 * - Statement-first, data on-demand
 * - Streaming animation for AI insights (ChatGPT-style)
 */

// Custom hook for streaming text animation
const useStreamingText = (fullText, isStreaming) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!isStreaming || !fullText) {
      setDisplayedText(fullText || '');
      setCurrentIndex(fullText?.length || 0);
      return;
    }

    if (currentIndex < fullText.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(fullText.substring(0, currentIndex + 1));
        setCurrentIndex(prev => prev + 1);
      }, 15); // 15ms per character for smooth typing effect

      return () => clearTimeout(timeout);
    }
  }, [fullText, currentIndex, isStreaming]);

  return displayedText;
};

// --- Smart AI Loader Component (Vertical Stepper) ---
const AILoader = () => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    { label: "Fetching student performance data...", duration: 4000 },
    { label: "Analyzing sectional cut-offs & trends...", duration: 15000 },
    { label: "Identifying historical patterns...", duration: 20000 },
    { label: "Evaluating question selection strategy...", duration: 25000 },
    { label: "Compiling evidence & proof tables...", duration: 25000 },
    { label: "Finalizing strategic insights...", duration: 30000 }
  ];

  useEffect(() => {
    let timeoutId;

    const processStep = (index) => {
      if (index >= steps.length) return;

      // Set current step
      setCurrentStep(index);

      // Schedule next step
      timeoutId = setTimeout(() => {
        processStep(index + 1);
      }, steps[index].duration);
    };

    // Start process
    processStep(0);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border border-gray-100">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 mb-4">
            <Zap className="w-6 h-6 text-ims-primary animate-pulse" />
          </div>
          <h3 className="text-xl font-bold text-ims-text-primary">
            Generating Comprehensive Report
          </h3>
          <p className="text-sm text-ims-text-secondary mt-2">
            AI is analyzing your performance deep-dive
          </p>
        </div>

        {/* Vertical Stepper */}
        <div className="space-y-4 relative pl-2">
          {/* Vertical connecting line */}
          <div className="absolute left-[1.65rem] top-4 bottom-4 w-0.5 bg-gray-100" />

          {steps.map((step, index) => {
            const isCompleted = index < currentStep;
            const isCurrent = index === currentStep;
            const isPending = index > currentStep;

            return (
              <div
                key={index}
                className={`relative flex items-center gap-4 p-3 rounded-xl transition-all duration-500 ${isCurrent ? 'bg-blue-50/50 border border-blue-100 shadow-sm translate-x-2' : ''
                  }`}
              >
                {/* Icon Marker */}
                <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors duration-300 ${isCompleted ? 'bg-ims-success border-ims-success' :
                  isCurrent ? 'bg-white border-ims-primary' :
                    'bg-white border-gray-200'
                  }`}>
                  {isCompleted && (
                    <CheckCircle className="w-4 h-4 text-white" strokeWidth={3} />
                  )}
                  {isCurrent && (
                    <div className="w-2.5 h-2.5 rounded-full bg-ims-primary animate-pulse" />
                  )}
                </div>

                {/* Text Content */}
                <div className="flex-1">
                  <p className={`text-sm font-medium transition-colors duration-300 ${isCompleted ? 'text-ims-text-secondary' :
                    isCurrent ? 'text-blue-900 font-semibold' :
                      'text-ims-text-muted'
                    }`}>
                    {step.label}
                  </p>
                  {isCurrent && (
                    <p className="text-xs text-ims-primary mt-0.5 animate-pulse">
                      Processing...
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-ims-text-muted">
            Estimated time: ~2 mins. Please do not close this window.
          </p>
        </div>
      </div>
    </div>
  );
};

const StatGenieReportCard = ({ selectedStudent, selectedTest, questionsData, students, tests, percentileData, studentCategory, onBack, jwtToken }) => {
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [testData, setTestData] = useState(null);
  const [detailedData, setDetailedData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [scorecardData, setScorecardData] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false); // Streaming state
  const [activeSectionTab, setActiveSectionTab] = useState('varc'); // Active tab for section breakdown
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false); // Mobile nav state
  const [activeSection, setActiveSection] = useState('diagnosis'); // Active section for nav
  
  // BLACKI / Non-BLACKI Mode State
  const [showModeSelection, setShowModeSelection] = useState(false);
  const [selectedMode, setSelectedMode] = useState(null); // 'BLACKI' or 'NON_BLACKI'
  const [currentViewMode, setCurrentViewMode] = useState('BLACKI'); // Currently displayed mode
  const [insightsByMode, setInsightsByMode] = useState({}); // { BLACKI: insights, NON_BLACKI: insights }
  const [isGeneratingSecondMode, setIsGeneratingSecondMode] = useState(false);
  const [bothModesReady, setBothModesReady] = useState(false);
  const [savedPreference, setSavedPreference] = useState(false);

  // Helper function to open evidence table modal
  // Uses tier-specific evidence based on current view mode
  const openEvidenceModal = (tableId, title) => {
    // Try tier-specific evidence first, then fall back to generic
    let tierEvidence = detailedData?.evidence?.[currentViewMode];
    
    // Fallback: if tier evidence doesn't exist or is empty, try the other structure
    if (!tierEvidence || Object.keys(tierEvidence).length === 0) {
      // Check if evidence has BLACKI/NON_BLACKI structure
      if (detailedData?.evidence?.BLACKI || detailedData?.evidence?.NON_BLACKI) {
        tierEvidence = detailedData.evidence[currentViewMode] || detailedData.evidence.BLACKI;
      } else {
        // Old structure - evidence tables directly
        tierEvidence = detailedData?.evidence;
      }
    }
    
    if (!tierEvidence) {
      console.warn(`Evidence tables not found`);
      return;
    }
    
    // Check if table exists directly
    if (tierEvidence[tableId]) {
      setActiveModal({ type: 'evidence', tableId, title });
      return;
    }
    
    // Fallback: check in nested structures (question_type_breakdown, area_performance, etc.)
    const nestedKeys = ['question_type_breakdown', 'area_performance', 'time_analysis'];
    for (const nestedKey of nestedKeys) {
      if (tierEvidence[nestedKey]) {
        // Check if tableId matches a section name in the nested structure
        const sectionKey = tableId.toLowerCase();
        if (tierEvidence[nestedKey][sectionKey]) {
          setActiveModal({ type: 'evidence', tableId: sectionKey, title, nestedKey });
          return;
        }
      }
    }
    
    // Final fallback: check evidenceGeneric
    const genericEvidence = detailedData?.evidenceGeneric;
    if (genericEvidence && genericEvidence[tableId]) {
      setActiveModal({ type: 'evidence', tableId, title, useGeneric: true });
      return;
    }
    
    console.warn(`Evidence table not found: ${tableId}`);
  };


  // Show mode selection modal when test is selected
  useEffect(() => {
    if (selectedStudent && selectedTest) {
      // Check if user has saved preference
      const savedMode = localStorage.getItem(`preferredMode_${selectedStudent}`);
      if (savedMode) {
        // Use saved preference, skip modal
        setSelectedMode(savedMode);
        setCurrentViewMode(savedMode);
        generateInsightsForMode(savedMode);
      } else {
        // Show mode selection modal
        setShowModeSelection(true);
      }
    }
  }, [selectedStudent, selectedTest, studentCategory]);

  // Handle mode selection from modal
  const handleModeSelect = (mode, rememberPreference) => {
    setSelectedMode(mode);
    setCurrentViewMode(mode);
    setShowModeSelection(false);
    setSavedPreference(rememberPreference);
    
    if (rememberPreference) {
      localStorage.setItem(`preferredMode_${selectedStudent}`, mode);
    }
    
    // Start generating first mode
    generateInsightsForMode(mode);
  };

  // Handle toggle between modes
  const handleModeToggle = () => {
    const newMode = currentViewMode === 'BLACKI' ? 'NON_BLACKI' : 'BLACKI';
    setCurrentViewMode(newMode);
    
    // If we already have insights for this mode, just switch
    if (insightsByMode[newMode]) {
      setAiInsights(insightsByMode[newMode]);
    }
    // Otherwise, the background generation should be in progress
  };

  // Force regenerate function for manual refresh
  const forceRegenerate = () => {

    generateInsights();
  };

  // Print report (sections are always visible, no need to expand)
  const expandAllForPrint = () => {
    // Wait for render then print
    setTimeout(() => {
      window.print();
    }, 300);
  };

  // Generate insights for a specific mode (BLACKI or NON_BLACKI)
  const generateInsightsForMode = async (mode) => {
    setLoading(true);
    setAiInsights(null);

    try {
      const cleanStudentPin = selectedStudent.replace('student_', '');

      const response = await fetch(`${API_BASE_URL}/api/ai/generate-insights?_t=${Date.now()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({
          studentPin: cleanStudentPin,
          testId: selectedTest,
          category: studentCategory || 'General',
          tier: mode, // 'BLACKI' or 'NON_BLACKI' - passed as tier to backend
          token: jwtToken || null  // JWT for API-first data fetch
        })
      });

      const data = await response.json();

      if (data.success) {
        // Store insights for this mode
        setInsightsByMode(prev => ({
          ...prev,
          [mode]: data
        }));

        // If this is the currently selected mode, display it
        if (mode === currentViewMode) {
          setIsStreaming(true);
          setAiInsights(data);
          setTestData(data.metadata);
          setDetailedData(data.detailedData);

          // Extract scorecard data from detailedData
          if (data.detailedData) {
            setScorecardData({
              elaborateData: data.detailedData.elaborateData || [],
              catReplicaData: data.detailedData.catReplicaData || [],
              graphicData: data.detailedData.graphicData || null
            });
          }

          // Store analytics data for passing to ScoreCard component
          setAnalyticsData({
            questionsData: data.detailedData?.questions || [],
            students: students,
            tests: tests,
            selectedStudent: selectedStudent,
            selectedTest: selectedTest,
            percentileData: {
              overall: data.detailedData?.overall?.percentile,
              varc: data.detailedData?.sections?.find(s => s.name === 'VARC')?.percentile,
              dilr: data.detailedData?.sections?.find(s => s.name === 'DILR')?.percentile,
              qa: data.detailedData?.sections?.find(s => s.name === 'QA')?.percentile
            },
            cutoffs: {
              overall: data.detailedData?.overall?.cutoff,
              varc: data.detailedData?.sections?.find(s => s.name === 'VARC')?.cutoff,
              dilr: data.detailedData?.sections?.find(s => s.name === 'DILR')?.cutoff,
              qa: data.detailedData?.sections?.find(s => s.name === 'QA')?.cutoff
            }
          });

          // Stop streaming after text is fully displayed
          setTimeout(() => {
            setIsStreaming(false);
          }, (data.insights?.length || 0) * 15 + 1000);
        }

        // After first mode completes, generate second mode in background
        const otherMode = mode === 'BLACKI' ? 'NON_BLACKI' : 'BLACKI';
        if (!insightsByMode[otherMode]) {
          setIsGeneratingSecondMode(true);
          setTimeout(() => {
            generateSecondMode(otherMode);
          }, 2000); // Small delay to let first mode render
        }
      } else {
        console.error('❌ API returned success: false', data);
      }
    } catch (error) {
      console.error('❌ Error generating insights:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate second mode in background (no loading state)
  const generateSecondMode = async (mode) => {
    try {
      const cleanStudentPin = selectedStudent.replace('student_', '');

      const response = await fetch(`${API_BASE_URL}/api/ai/generate-insights?_t=${Date.now()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({
          studentPin: cleanStudentPin,
          testId: selectedTest,
          category: studentCategory || 'General',
          tier: mode,
          token: jwtToken || null  // JWT for API-first data fetch
        })
      });

      const data = await response.json();

      if (data.success) {
        setInsightsByMode(prev => ({
          ...prev,
          [mode]: data
        }));
        setIsGeneratingSecondMode(false);
        setBothModesReady(true);
      }
    } catch (error) {
      console.error('❌ Error generating second mode:', error);
      setIsGeneratingSecondMode(false);
    }
  };

  // Legacy function for backward compatibility
  const generateInsights = () => {
    // Check for saved preference or show modal
    const savedMode = localStorage.getItem(`preferredMode_${selectedStudent}`);
    if (savedMode) {
      setSelectedMode(savedMode);
      setCurrentViewMode(savedMode);
      generateInsightsForMode(savedMode);
    } else {
      setShowModeSelection(true);
    }
  };

  // Parse AI insights into accordion-friendly structure
  const parseInsights = (text) => {
    if (!text) {

      return null;
    }



    // Split by ## headers (exactly 2 hashes) to get main sections - NOT ### subsections
    const sections = text.split(/^##\s+/m).filter(s => s.trim());



    // Extract the 3 main sections
    const diagnosis = sections.find(s => s.match(/1\.\s*(?:THE DIAGNOSIS|YOUR DIAGNOSIS.*TRAJECTORY)/i));
    const sectionBreakdown = sections.find(s => s.match(/2\.\s*SECTION.*BREAKDOWN/i));
    const actionPlan = sections.find(s => s.match(/3\.\s*.*ACTION PLAN/i));



    // Parse individual sections (VARC, DILR, QA) from Section Breakdown
    // Use the raw text instead of the extracted sectionBreakdown to preserve ### markers
    let sectionDetails = { varc: null, dilr: null, qa: null };

    // Extract from the full raw text to preserve the ### markers
    const sectionBreakdownMatch = text.match(/##\s*2\.\s*SECTION.*BREAKDOWN([\s\S]*?)(?=##\s*3\.|$)/i);

    if (sectionBreakdownMatch) {
      const fullSectionContent = sectionBreakdownMatch[1];


      const varcMatch = fullSectionContent.match(/###\s*VARC(?:\s+Analysis)?([\s\S]*?)(?=###\s*(?:DILR|QA)|---END OF VARC ANALYSIS---|$)/i);
      const dilrMatch = fullSectionContent.match(/###\s*DILR(?:\s+Analysis)?([\s\S]*?)(?=###\s*(?:VARC|QA)|---END OF DILR ANALYSIS---|$)/i);
      const qaMatch = fullSectionContent.match(/###\s*QA(?:\s+Analysis)?([\s\S]*?)(?=###\s*(?:VARC|DILR)|---END OF QA ANALYSIS---|$)/i);



      if (varcMatch) {
        sectionDetails.varc = varcMatch[1].trim();

      }
      if (dilrMatch) {
        sectionDetails.dilr = dilrMatch[1].trim();

      }
      if (qaMatch) {
        sectionDetails.qa = qaMatch[1].trim();

      }
    }

    const parsed = {
      diagnosis: diagnosis?.replace(/1\.\s*(?:THE DIAGNOSIS|YOUR DIAGNOSIS.*TRAJECTORY)/i, '').trim(),
      sectionBreakdown: sectionBreakdown?.replace(/2\.\s*SECTION.*BREAKDOWN/i, '').trim(),
      sectionDetails,
      actionPlan: actionPlan?.replace(/3\.\s*.*ACTION PLAN/i, '').trim(),
      raw: text,
      allSections: sections
    };







    return parsed;
  };

  const insights = parseInsights(aiInsights?.insights);

  // Scroll-aware active section tracking with IntersectionObserver
  useEffect(() => {
    // Only setup observer when insights are loaded and sections exist
    if (!insights?.diagnosis) return;

    const sectionIds = ['diagnosis', 'breakdown', 'actionplan'];
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Small delay to ensure DOM elements are rendered
    const timeoutId = setTimeout(() => {
      sectionIds.forEach(id => {
        const element = document.getElementById(id);
        if (element) observer.observe(element);
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [insights]);

  if (loading) {
    return <AILoader />;
  }

  if (!selectedStudent || !selectedTest) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center max-w-md">
          <Award className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">Select a Test</h3>
          <p className="text-slate-500">Choose a student and test to view your StatGenie analysis</p>
        </div>
      </div>
    );
  }

  // Navigation items (matching accordion headers)
  const navItems = [
    { id: 'diagnosis', label: 'Diagnosis', icon: Activity },
    { id: 'breakdown', label: 'Section Breakdown', icon: BarChart3 },
    { id: 'actionplan', label: 'Action Plan', icon: Target },
  ];

  // ========================================
  // SUMMARY CARDS DERIVATION
  // ========================================
  const deriveSummaryCards = () => {
    const sections = detailedData?.sections || [];
    const overall = detailedData?.overall;
    
    // 1. CUTOFF STATUS
    const clearedCount = sections.filter(s => s.cutoff_status === 'CLEARED').length;
    const totalSections = sections.length || 3;
    
    let cutoffStatus = 'Not Cleared';
    let cutoffStatusColor = 'red';
    
    if (clearedCount === totalSections) {
      // All cleared - check buffer
      const minBuffer = Math.min(...sections
        .filter(s => s.percentile && s.cutoff_percentile)
        .map(s => s.percentile - s.cutoff_percentile)
        .filter(b => !isNaN(b))
      );
      
      if (minBuffer >= 15) {
        cutoffStatus = 'Strong';
        cutoffStatusColor = 'green';
      } else if (minBuffer >= 5) {
        cutoffStatus = 'Safe';
        cutoffStatusColor = 'green';
      } else {
        cutoffStatus = 'Borderline';
        cutoffStatusColor = 'amber';
      }
    } else if (clearedCount === totalSections - 1) {
      cutoffStatus = 'Sectional Risk';
      cutoffStatusColor = 'amber';
    } else if (clearedCount > 0) {
      cutoffStatus = 'Multiple Risks';
      cutoffStatusColor = 'orange';
    } else {
      cutoffStatus = 'Not Cleared';
      cutoffStatusColor = 'red';
    }

    // 2. SECTION SIGNAL (Strongest/Weakest)
    const sortedSections = [...sections].sort((a, b) => (b.percentile || 0) - (a.percentile || 0));
    const strongest = sortedSections[0]?.name || 'VARC';
    const weakest = sortedSections[sortedSections.length - 1]?.name || 'DILR';

    // 3. MAIN ACTION (Extract from Action Plan section)
    let mainAction = 'Review performance analysis';
    if (insights?.actionPlan) {
      // Try to extract first actionable recommendation
      const actionMatch = insights.actionPlan.match(/\*\*(.+?)\*\*/);
      if (actionMatch) {
        mainAction = actionMatch[1].replace(/^\d+\.\s*/, '').substring(0, 40);
      } else {
        // Fallback: get first sentence
        const firstSentence = insights.actionPlan.split(/[.\n]/)[0];
        if (firstSentence && firstSentence.length > 10) {
          mainAction = firstSentence.substring(0, 40) + '...';
        }
      }
    }

    // 4. ONE-LINE DIAGNOSIS
    let oneLineDiagnosis = 'Analyzing your performance...';
    if (insights?.diagnosis) {
      // Extract first meaningful sentence from diagnosis
      const sentences = insights.diagnosis
        .replace(/\*\*/g, '')
        .split(/[.!?]\s+/)
        .filter(s => s.length > 20 && !s.startsWith('#'));
      if (sentences.length > 0) {
        oneLineDiagnosis = sentences[0].substring(0, 120);
        if (sentences[0].length > 120) oneLineDiagnosis += '...';
      }
    }

    return {
      cutoffStatus,
      cutoffStatusColor,
      strongest,
      weakest,
      mainAction,
      oneLineDiagnosis
    };
  };

  const summaryCards = detailedData ? deriveSummaryCards() : null;

  // Get tier-specific evidence based on current view mode
  // Handle both new nested structure {BLACKI: {...}, NON_BLACKI: {...}} and old flat structure
  const currentEvidence = (() => {
    const evidence = detailedData?.evidence;
    if (!evidence) return null;
    
    // Check if evidence has tier structure
    if (evidence.BLACKI || evidence.NON_BLACKI) {
      return evidence[currentViewMode] || evidence.BLACKI || null;
    }
    
    // Old flat structure - return as-is
    return evidence;
  })();

  // Scroll to section
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    // Scroll to element
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsMobileNavOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-ims">
      {/* Mode Selection Modal */}
      <ModeSelectionModal
        isOpen={showModeSelection}
        onSelect={handleModeSelect}
        onClose={() => setShowModeSelection(false)}
        studentCategory={studentCategory}
      />

      {/* Sticky Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Hamburger + Logo */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Toggle navigation"
              >
                <Menu className="w-6 h-6 text-ims-text-primary" />
              </button>
              {/* IMS Logo - Official */}
              <div className="flex items-center gap-2">
                <img 
                  src="https://www.imsindia.com/ims-india/wp-content/themes/ims-india-new/img/icons/logo.svg" 
                  alt="IMS India" 
                  height="32" 
                  className="h-8"
                />
                <span className="font-semibold text-ims-text-primary hidden sm:block">AI Analytics</span>
              </div>
            </div>

            {/* Center: Page Title + Mode Toggle */}
            {testData && (
              <div className="hidden md:flex items-center gap-4">
                <div className="text-center">
                  <p className="text-sm font-semibold text-ims-text-primary">{testData.test_name}</p>
                  <p className="text-xs text-ims-text-secondary">StatGenie Report</p>
                </div>
                {/* Mode Toggle - Only show when insights are loaded */}
                {aiInsights && (
                  <ModeToggle
                    currentMode={currentViewMode}
                    onToggle={handleModeToggle}
                    isBothReady={bothModesReady}
                    isGenerating={isGeneratingSecondMode}
                  />
                )}
              </div>
            )}

            {/* Right: Test Info + Regenerate */}
            <div className="flex items-center gap-3">
              {testData && (
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium text-ims-text-primary">{testData.test_name}</p>
                  <p className="text-xs text-ims-text-secondary">{studentCategory} Category</p>
                </div>
              )}
              <button
                onClick={forceRegenerate}
                disabled={loading}
                className="px-3 py-2 bg-ims-primary text-white rounded-lg text-sm font-medium hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
                title="Regenerate AI insights"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{loading ? 'Generating...' : 'Regenerate'}</span>
              </button>
              
              {/* Print Button - For Admin Use Only */}
              <button
                onClick={expandAllForPrint}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 active:bg-gray-300 flex items-center gap-2 transition-all no-print"
                title="Print full report with all tables"
              >
                <Printer className="w-4 h-4" />
                <span className="hidden sm:inline">Print Full Report</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Print Header - Only visible when printing */}
      <div className="hidden print:block bg-white border-b border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <img 
              src="https://www.imsindia.com/ims-india/wp-content/themes/ims-india-new/img/icons/logo.svg" 
              alt="IMS India" 
              className="h-10 mb-2"
            />
            <h1 className="text-2xl font-bold text-ims-text-primary">AI Analytics Report</h1>
          </div>
          <div className="text-right">
            <p className="text-lg font-semibold text-ims-text-primary">{testData?.test_name || 'Test Report'}</p>
            <p className="text-sm text-ims-text-secondary">Category: {studentCategory}</p>
            <p className="text-xs text-ims-text-muted mt-1">Generated on {new Date().toLocaleDateString('en-IN')}</p>
          </div>
        </div>
      </div>

      {/* ========================================
         TOP SUMMARY BAND + STICKY JUMP BAR
         ======================================== */}
      
      {/* Mobile Navigation Overlay */}
      {isMobileNavOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileNavOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 w-64 bg-white z-50 md:hidden shadow-xl">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <span className="font-semibold text-ims-text-primary">Navigation</span>
              <button
                onClick={() => setIsMobileNavOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5 text-ims-text-secondary" />
              </button>
            </div>
            <nav className="p-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left font-medium transition-all ${
                      activeSection === item.id
                        ? 'bg-blue-50 text-ims-primary border-l-4 border-ims-primary'
                        : 'text-ims-text-secondary hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </aside>
        </>
      )}

      {/* Main Content Container */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* TOP SUMMARY SECTION */}
        {summaryCards && (
          <>
            {/* One-Line Diagnosis Band */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6 animate-fade-in">
              <p className="text-base md:text-lg font-medium text-ims-text-primary leading-relaxed">
                {summaryCards.oneLineDiagnosis}
              </p>
            </div>

            {/* Three Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Cutoff Status Card */}
              <div 
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 cursor-pointer hover:shadow-md transition-shadow animate-fade-in"
                onClick={() => scrollToSection('diagnosis')}
              >
                <p className="text-xs font-medium text-ims-text-muted uppercase tracking-wider mb-2">Cutoff Status</p>
                <p className={`text-xl font-bold ${
                  summaryCards.cutoffStatusColor === 'green' ? 'text-ims-success' :
                  summaryCards.cutoffStatusColor === 'amber' ? 'text-amber-600' :
                  summaryCards.cutoffStatusColor === 'orange' ? 'text-orange-600' :
                  'text-ims-error'
                }`}>
                  {summaryCards.cutoffStatus}
                </p>
              </div>

              {/* Section Signal Card */}
              <div 
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 cursor-pointer hover:shadow-md transition-shadow animate-fade-in"
                onClick={() => scrollToSection('breakdown')}
              >
                <p className="text-xs font-medium text-ims-text-muted uppercase tracking-wider mb-2">Section Signal</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-ims-success font-semibold">Strongest: {summaryCards.strongest}</span>
                  <span className="text-gray-300">|</span>
                  <span className="text-sm text-ims-error font-semibold">Weakest: {summaryCards.weakest}</span>
                </div>
              </div>

              {/* Main Action Card */}
              <div 
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 cursor-pointer hover:shadow-md transition-shadow animate-fade-in"
                onClick={() => scrollToSection('actionplan')}
              >
                <p className="text-xs font-medium text-ims-text-muted uppercase tracking-wider mb-2">Main Action</p>
                <p className="text-base font-semibold text-ims-primary">
                  {summaryCards.mainAction}
                </p>
              </div>
            </div>
          </>
        )}

        {/* Sticky Horizontal Jump Bar */}
        {summaryCards && (
          <div className="sticky top-16 bg-white z-30 border-b border-gray-200 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 mb-6 no-print">
            <div className="flex gap-1 sm:gap-2 overflow-x-auto">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                      activeSection === item.id
                        ? 'bg-ims-primary text-white'
                        : 'text-ims-text-secondary hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <main className="space-y-6">

          {/* ============================================
              SECTION 1: DIAGNOSIS
              Always-visible section card (no accordion)
              ============================================ */}
          {insights?.diagnosis && (
            <section 
              id="diagnosis" 
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              {/* Section Header */}
              <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50/50 to-white">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-ims-primary/10">
                    <AlertTriangle className="w-5 h-5 text-ims-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-ims-text-primary">Cut-offs & Performance Trends</h2>
                    <p className="text-sm text-ims-text-secondary">Current Performance + Historical Patterns</p>
                  </div>
                  {/* Dynamic Badge */}
                  {summaryCards && (
                    <span className={`ml-auto px-3 py-1 rounded-full text-sm font-medium ${
                      summaryCards.cutoffStatusColor === 'green' ? 'bg-green-100 text-green-700' :
                      summaryCards.cutoffStatusColor === 'amber' ? 'bg-amber-100 text-amber-700' :
                      summaryCards.cutoffStatusColor === 'orange' ? 'bg-orange-100 text-orange-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {summaryCards.cutoffStatus}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Section Content - Always Visible */}
              <div className="p-6">
                <div className="space-y-6">
                  <ContentWithProofs
                    content={parseContentWithProofs(insights.diagnosis)}
                    evidence={currentEvidence}
                    onOpenEvidence={openEvidenceModal}
                  />
                </div>
                
                {/* Evidence & Scorecard Links */}
                <div className="mt-4 flex gap-4 items-center text-sm">
                  <button
                    type="button"
                    onClick={() => openEvidenceModal('historical_cutoff_pattern', 'Historical Cutoff Pattern (Last 5 Tests)')}
                    className="text-ims-primary hover:text-ims-primary-dark font-medium flex items-center gap-1 transition-colors"
                  >
                    <BarChart3 className="w-4 h-4" />
                    View Historical Cutoff Pattern
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveModal('scorecard')}
                    className="text-ims-primary hover:text-ims-primary-dark font-medium flex items-center gap-1 transition-colors"
                  >
                    <Activity className="w-4 h-4" />
                    View Full Scorecard
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* ============================================
              SECTION 2: SECTION BREAKDOWN
              Always-visible section card with VARC/DILR/QA tabs
              ============================================ */}
          {(insights?.sectionBreakdown || insights?.sectionDetails?.varc || insights?.sectionDetails?.dilr || insights?.sectionDetails?.qa) && (
            <section 
              id="breakdown" 
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              {/* Section Header */}
              <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50/50 to-white">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-ims-primary/10">
                    <Activity className="w-5 h-5 text-ims-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-ims-text-primary">Section-by-Section Breakdown</h2>
                    <p className="text-sm text-ims-text-secondary">VARC • DILR • QA Performance</p>
                  </div>
                </div>
              </div>
              
              {/* Section Content - Always Visible */}
              <div className="p-6">
                {/* Tab Interface for Sections */}
                <div className="space-y-4">
                  {/* Tab Headers */}
                  <div className="flex gap-2 border-b border-gray-200 -mb-4 pb-4">
                    <button
                      onClick={() => setActiveSectionTab('varc')}
                      className={`px-5 py-2.5 font-semibold text-sm rounded-lg transition-all ${
                        activeSectionTab === 'varc'
                          ? 'bg-ims-primary text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      VARC
                    </button>
                    <button
                      onClick={() => setActiveSectionTab('dilr')}
                      className={`px-5 py-2.5 font-semibold text-sm rounded-lg transition-all ${
                        activeSectionTab === 'dilr'
                          ? 'bg-ims-primary text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      DILR
                    </button>
                    <button
                      onClick={() => setActiveSectionTab('qa')}
                      className={`px-5 py-2.5 font-semibold text-sm rounded-lg transition-all ${
                        activeSectionTab === 'qa'
                          ? 'bg-ims-primary text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      QA
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="pt-6">
                    {activeSectionTab === 'varc' && insights.sectionDetails?.varc && (
                      <div className="space-y-6 animate-fade-in">
                        <ContentWithProofs
                          content={parseContentWithProofs(insights.sectionDetails.varc)}
                          evidence={currentEvidence}
                          onOpenEvidence={openEvidenceModal}
                        />
                      </div>
                    )}

                    {activeSectionTab === 'dilr' && insights.sectionDetails?.dilr && (
                      <div className="space-y-6 animate-fade-in">
                        <ContentWithProofs
                          content={parseContentWithProofs(insights.sectionDetails.dilr)}
                          evidence={currentEvidence}
                          onOpenEvidence={openEvidenceModal}
                        />
                      </div>
                    )}

                    {activeSectionTab === 'qa' && insights.sectionDetails?.qa && (
                      <div className="space-y-6 animate-fade-in">
                        <ContentWithProofs
                          content={parseContentWithProofs(insights.sectionDetails.qa)}
                          evidence={currentEvidence}
                          onOpenEvidence={openEvidenceModal}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* ============================================
              SECTION 3: ACTION PLAN
              Always-visible section card
              ============================================ */}
          {insights?.actionPlan && (
            <section 
              id="actionplan" 
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              {/* Section Header */}
              <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50/50 to-white">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-ims-primary/10">
                    <Zap className="w-5 h-5 text-ims-primary" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-ims-text-primary">Your Action Plan</h2>
                    <p className="text-sm text-ims-text-secondary">Next Test Strategy</p>
                  </div>
                  {/* Main Action Summary */}
                  {summaryCards && (
                    <span className="ml-auto px-3 py-1 rounded-lg bg-ims-primary text-white text-sm font-medium">
                      {summaryCards.mainAction}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Section Content - Always Visible */}
              <div className="p-6">
                <div className="space-y-6">
                  <ContentWithProofs
                    content={parseContentWithProofs(insights.actionPlan)}
                    evidence={currentEvidence}
                    onOpenEvidence={openEvidenceModal}
                  />
                </div>
              </div>
            </section>
          )}

        </main>
      </div>

      {/* PRINT-ONLY: All Evidence Tables Section */}
      {currentEvidence && (
        <div className="print-only-section hidden print:block bg-white p-8">
          <h2 className="text-2xl font-bold text-ims-text-primary mb-6 pb-2 border-b-2 border-ims-primary">
            Appendix: All Evidence Tables ({currentViewMode} Mode)
          </h2>
          {Object.entries(currentEvidence).map(([tableId, tableData]) => (
            <div key={tableId} className="mb-8 page-break-inside-avoid">
              <h3 className="text-lg font-semibold text-ims-text-primary mb-3">
                {tableData.title || tableId}
              </h3>
              {tableData.columns && tableData.rows && (
                <table className="w-full border-collapse border border-gray-300 text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      {tableData.columns.map((col, idx) => (
                        <th key={idx} className="border border-gray-300 px-3 py-2 text-left">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.rows.map((row, rowIdx) => (
                      <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        {row.map((cell, cellIdx) => (
                          <td key={cellIdx} className="border border-gray-300 px-3 py-2">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}

      {/* IMS Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Footer Links */}
          <div className="flex flex-wrap justify-center gap-6 mb-6">
            <a href="/blog/" className="text-sm text-ims-text-secondary hover:text-ims-primary transition-colors">Blog</a>
            <a href="/faqs/" className="text-sm text-ims-text-secondary hover:text-ims-primary transition-colors">FAQs</a>
            <a href="/about-us/" className="text-sm text-ims-text-secondary hover:text-ims-primary transition-colors">About Us</a>
            <a href="https://imsindiahelp.freshdesk.com/" target="_blank" rel="noopener noreferrer" className="text-sm text-ims-text-secondary hover:text-ims-primary transition-colors">Help & Support</a>
            <a href="/privacy-policy/" className="text-sm text-ims-text-secondary hover:text-ims-primary transition-colors">Privacy</a>
            <a href="/csr-policy/" className="text-sm text-ims-text-secondary hover:text-ims-primary transition-colors">CSR Policy</a>
            <a href="/disclaimer/" className="text-sm text-ims-text-secondary hover:text-ims-primary transition-colors">Disclaimer</a>
            <a href="/terms-of-use/" className="text-sm text-ims-text-secondary hover:text-ims-primary transition-colors">Terms Of Use</a>
          </div>
          
          {/* Social Links */}
          <div className="flex justify-center items-center gap-4 mb-6">
            <span className="text-sm text-ims-text-secondary">Follow us on</span>
            <a href="https://facebook.com/imsindia" target="_blank" rel="noopener noreferrer" className="text-ims-text-secondary hover:text-ims-primary transition-colors">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M15 3a11.993 11.993 0 0 0-1.794 23.852V18.18H10.237V15.026h2.969v-2.1c0-3.475 1.693-5 4.581-5a16.845 16.845 0 0 1 2.461.149v2.753h-1.97c-1.226 0-1.654 1.163-1.654 2.473v1.724h3.593L19.73 18.18H16.624v8.7A11.993 11.993 0 0 0 15 3z"/></svg>
            </a>
            <a href="https://twitter.com/ims_india" target="_blank" rel="noopener noreferrer" className="text-ims-text-secondary hover:text-ims-primary transition-colors">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a href="https://www.linkedin.com/company/ims-learning-resources" target="_blank" rel="noopener noreferrer" className="text-ims-text-secondary hover:text-ims-primary transition-colors">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="12"/><path d="M10.272 18.981H7.946V11.495h2.327zm-1.186-9.557a1.356 1.356 0 1 1 1.356-1.357 1.356 1.356 0 0 1-1.356 1.357zm9.9 8.556h-2.327V15.34c0-.868-.016-1.985-1.209-1.985-1.211 0-1.4.946-1.4 1.922v3.7h-2.327V11.495h2.232v1.023h.028a2.445 2.445 0 0 1 2.2-1.209c2.356 0 2.791 1.551 2.791 3.566z" fill="white"/></svg>
            </a>
            <a href="https://www.instagram.com/ims.india/" target="_blank" rel="noopener noreferrer" className="text-ims-text-secondary hover:text-ims-primary transition-colors">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="12"/><path d="M9.265 5A4.273 4.273 0 0 0 5 9.265v5.563a4.273 4.273 0 0 0 4.265 4.265h5.563a4.274 4.274 0 0 0 4.265-4.265V9.265A4.273 4.273 0 0 0 14.828 5zm0 1.113h5.563A3.144 3.144 0 0 1 17.98 9.265v5.563a3.144 3.144 0 0 1-3.152 3.152H9.265a3.144 3.144 0 0 1-3.152-3.152V9.265A3.144 3.144 0 0 1 9.265 6.113zm6.49 1.487a.742.742 0 1 0 .742.742.741.741 0 0 0-.742-.742zm-3.709.742a3.708 3.708 0 1 0 3.708 3.708 3.717 3.717 0 0 0-3.708-3.708zm0 1.113a2.6 2.6 0 1 1-2.6 2.6 2.588 2.588 0 0 1 2.6-2.6z" fill="white"/></svg>
            </a>
            <a href="https://www.youtube.com/channel/UCjX-3wCGEPW3DzlLR35pB7g" target="_blank" rel="noopener noreferrer" className="text-ims-text-secondary hover:text-ims-primary transition-colors">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="12"/><path d="M7.793 4a34.622 34.622 0 0 0-4.527.242A1.45 1.45 0 0 0 2.242 5.266 20.65 20.65 0 0 0 2 8.634a20.65 20.65 0 0 0 .242 3.368 1.45 1.45 0 0 0 1.024 1.024 34.623 34.623 0 0 0 4.527.242 34.623 34.623 0 0 0 4.527-.242A1.451 1.451 0 0 0 13.344 12a20.651 20.651 0 0 0 .242-3.368 20.65 20.65 0 0 0-.242-3.368A1.451 1.451 0 0 0 12.32 4.242 34.623 34.623 0 0 0 7.793 4zm0 1.159a31.273 31.273 0 0 1 4.227.2.294.294 0 0 1 .2.2 18.555 18.555 0 0 1 .2 3.068 18.56 18.56 0 0 1-.2 3.068.294.294 0 0 1-.2.2 31.267 31.267 0 0 1-4.227.2 31.286 31.286 0 0 1-4.227-.2.294.294 0 0 1-.2-.2 18.555 18.555 0 0 1-.2-3.068 18.579 18.579 0 0 1 .2-3.07.293.293 0 0 1 .2-.2A31.267 31.267 0 0 1 7.793 5.159zM6.634 6.627v4.014L10.11 8.634z" fill="white"/></svg>
            </a>
          </div>
          
          {/* Copyright */}
          <div className="border-t border-gray-200 pt-6 text-center">
            <p className="text-sm text-ims-text-muted">
              © {new Date().getFullYear()} IMS Learning Resources Pvt. Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals with Integrated Components */}
      {activeModal && (
        <Modal onClose={() => setActiveModal(null)}>
          <ModalContent
            key={`${activeModal.type}-${activeModal.tableId || ''}-${currentViewMode}`}
            type={activeModal}
            testData={testData}
            detailedData={detailedData}
            analyticsData={analyticsData}
            scorecardData={scorecardData}
            studentCategory={studentCategory}
            currentViewMode={currentViewMode}
          />
        </Modal>
      )}
      
    </div >
  );
};

// Diagnosis Section Component with Streaming and Dynamic Badge
const DiagnosisSection = ({ diagnosis, isStreaming, onToggle, isOpen, onModalOpen, detailedData, onOpenEvidence }) => {
  // Don't use streaming for diagnosis - just show full text immediately
  const streamedText = diagnosis; // Remove streaming animation



  // Helper to open historical cutoff pattern table
  const openHistoricalPattern = () => {
    // Open the historical_cutoff_pattern evidence table
    onOpenEvidence?.('historical_cutoff_pattern', 'Historical Cutoff Pattern (Last 5 Tests)');
  };

  // Extract dynamic badge from diagnosis content or detailedData
  const detectBadge = () => {
    // Check if we have overall cutoff data
    const overall = detailedData?.overall;
    const sections = detailedData?.sections || [];

    if (overall && sections.length > 0) {
      // Count how many cutoffs are cleared
      const clearedCount = sections.filter(s => s.cutoff_status === 'CLEARED').length;
      const totalSections = sections.length;

      // Check percentile buffer for cleared sections
      const minBuffer = Math.min(...sections
        .filter(s => s.cutoff_status === 'CLEARED' && s.percentile && s.cutoff_percentile)
        .map(s => s.percentile - s.cutoff_percentile)
      );

      if (clearedCount === totalSections) {
        // All cutoffs cleared
        if (minBuffer >= 15) {
          return { text: 'Strong', color: 'green' };
        } else if (minBuffer >= 5) {
          return { text: 'Safe', color: 'green' };
        } else {
          return { text: 'Safe', color: 'amber' }; // At risk of dropping
        }
      } else if (clearedCount >= totalSections - 1) {
        // 1 cutoff missed
        return { text: 'Warning', color: 'amber' };
      } else {
        // 2+ cutoffs missed
        return { text: 'Critical', color: 'red' };
      }
    }

    // Fallback: Try to detect from text
    if (streamedText) {
      if (/all.*cutoff.*cleared/i.test(streamedText) && /strong|excellent|good buffer/i.test(streamedText)) {
        return { text: 'Strong', color: 'green' };
      } else if (/all.*cutoff.*cleared/i.test(streamedText)) {
        return { text: 'Safe', color: 'green' };
      } else if (/1.*cutoff.*missed|warning/i.test(streamedText)) {
        return { text: 'Warning', color: 'amber' };
      }
    }

    // Default to critical if can't determine
    return { text: 'Critical', color: 'red' };
  };

  const badge = detectBadge();

  return (
    <AccordionSection
      id="diagnosis"
      icon={<AlertTriangle className="w-5 h-5" />}
      title="Cut-offs & Performance Trends"
      subtitle="Current Performance + Historical Patterns"
      iconColor="indigo"
      isOpen={isOpen}
      onToggle={onToggle}
      badge={badge}
    >
      <div className="space-y-6">
        <ContentWithProofs
          content={parseContentWithProofs(streamedText)}
          evidence={currentEvidence}
          onOpenEvidence={onOpenEvidence}
        />
      </div>

      <div className="mt-4 flex gap-4 items-center text-sm">
        <button
            type="button"
            onClick={openHistoricalPattern}
            className="ai-evidence-link"
          >
            <BarChart3 className="w-3.5 h-3.5" />
            Historical Pattern Evidence
          </button>
        <button
            type="button"
            onClick={onModalOpen}
            className="ai-evidence-link"
          >
            <Activity className="w-3.5 h-3.5" />
            View Full Scorecard
          </button>
      </div>
    </AccordionSection>
  );
};

// Component to render content with expandable proofs
const ContentWithProofs = ({ content, isStreaming, evidence, onOpenEvidence }) => {
  const [expandedProofs, setExpandedProofs] = useState(new Set());

  const toggleProof = (index) => {
    setExpandedProofs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Regex to detect table proof format: [[TABLE:id|Title]]
  const TABLE_PROOF_RE = /^\[\[TABLE:([a-zA-Z0-9_\-]+)\|([^\]]+)\]\]$/i;

  // Helper to process text content with enhanced formatting
  const processTextContent = (text) => {
    if (!text) return null;

    let processedHtml = text;

    // --- Pre-processing ---

    // Bold text: **text** -> <span class="ai-emphasis">text</span>
    processedHtml = processedHtml.replace(
      /\*\*([^*]+)\*\*/g,
      '<span class="ai-emphasis">$1</span>'
    );

    // Italic text: *text* -> <em>text</em>
    processedHtml = processedHtml.replace(
      /\*([^*]+)\*/g,
      '<em class="text-slate-600">$1</em>'
    );


    // --- Remove redundant labels at start (Critical:, Verdict:) ---
    // These are already shown in the badge, no need to repeat
    // Remove if it's on its own line or followed by content
    processedHtml = processedHtml.replace(/^(Critical|Verdict):?\s*\n?/im, '');
    processedHtml = processedHtml.replace(/^\*\*?(Critical|Verdict):?\*\*?\s*\n?/im, '');

    // --- Remove unwanted headings ---
    // Remove "Let's understand the root causes" and similar
    processedHtml = processedHtml.replace(/^Let'?s?\s+(understand|explore)\s+the\s+root\s+causes?\.?\s*\n?/gmi, '');

    // --- Section Headings ---
    // "BLOCK 1 - ...", "### Heading", or "Current Test Performance"
    processedHtml = processedHtml.replace(
      /^(BLOCK \\d+[\s—\-–]+[^\n]+|###\s+[^\n]+|Current Test Performance|Historical Cut-?off Pattern|Strategic Context)/gmi,
      (match) => {
        let title = match.replace(/^###\s+/, '');
        // Remove "BLOCK X - " prefix (handle various dash types and spacing)
        title = title.replace(/^BLOCK\s+\d+[\s—\-–:]+/i, '');
        // Also handle when it's in bold format
        title = title.replace(/^\*\*BLOCK\s+\d+[\s—\-–:]+\*\*/i, '');
        return `<div class="ai-section-heading">${title}</div>`;
      }
    );

    // --- Also remove standalone BLOCK headings that might be formatted differently ---
    processedHtml = processedHtml.replace(/^BLOCK\s+\d+[\s—\-–:]+[^\n]*$/gmi, (match) => {
      const title = match.replace(/^BLOCK\s+\d+[\s—\-–:]+/i, '');
      if (title.trim()) {
        return `<div class="ai-section-heading">${title}</div>`;
      }
      return ''; // Remove if nothing left after BLOCK removal
    });

    // --- Special Tags/Labels ---
    // Verdict:, Warning:, etc.
    const tags = [
      { key: 'Verdict', class: 'verdict' },
      { key: 'Warning', class: 'warning' },
      { key: 'Critical', class: 'warning' },
      { key: 'Note', class: 'note' },
      { key: 'Important', class: 'note' },
      { key: 'Recommendation', class: 'strategy' },
      { key: 'Strategy', class: 'strategy' },
      { key: 'Key Insight', class: 'insight' },
      { key: 'Observation', class: 'insight' }
    ];

    tags.forEach(({ key, class: className }) => {
      const regex = new RegExp(`^(${key}):`, 'gim');
      processedHtml = processedHtml.replace(
        regex,
        `<span class="ai-tag ${className}">$1</span>`
      );
      // Also handle inline cases like "**Verdict:**" which might have been processed by bolding
      const inlineRegex = new RegExp(`<span class="ai-emphasis">(${key}):?</span>:?`, 'gim');
      processedHtml = processedHtml.replace(
        inlineRegex,
        `<span class="ai-tag ${className}">$1</span>`
      );
    });

    // --- Lists (Bullet Points) ---
    const lines = processedHtml.split('\n');
    let output = [];
    let inList = false;

    lines.forEach((line, i) => {
      const trimmed = line.trim();

      // Check for bullet points
      const isBullet = /^[•\-\*]\s+/.test(trimmed);
      const isNumber = /^\d+\.\s+/.test(trimmed);

      // Force section labels (Overall:, VARC:, DILR:, QA:) as bullets
      const isSectionLabel = /^(Overall|VARC|DILR|QA):/i.test(trimmed);

      // Check for our custom headings (divs)
      const isHeading = trimmed.startsWith('<div class="ai-section-heading">');

      // Check for empty lines
      const isEmpty = trimmed === '';

      // Detect summary/insight sentences (e.g., "You miss cut-offs...")
      const isSummary = /^(You (miss|cleared?|are at)|Without clearing|All (three )?sectional cut-?offs)/i.test(trimmed);

      if (isHeading) {
        if (inList) { inList = false; } // Close list if open
        output.push(trimmed);
        return;
      }

      if (isSummary) {
        // Highlight summary sentences
        if (inList) { inList = false; }
        output.push(`<p class="mb-3 mt-2 text-slate-800 font-semibold leading-relaxed">${trimmed}</p>`);
        return;
      }

      if (isBullet || isNumber || isSectionLabel) {
        inList = true;
        let content = '';
        let bullet = '';

        if (isSectionLabel) {
          // Extract section label content
          const match = trimmed.match(/^(Overall|VARC|DILR|QA):(.+)$/i);
          if (match) {
            content = `<span class="font-medium">${match[1]}:</span>${match[2]}`;
            bullet = '•';
          }
        } else if (isBullet) {
          content = trimmed.replace(/^[•\-\*]\s+/, '');
          bullet = '•';
        } else {
          const match = trimmed.match(/^(\d+)\.\s+(.+)$/);
          if (match) {
            bullet = match[1];
            content = match[2];
          }
        }

        output.push(
          `<div class="ai-list-item">
            <div class="ai-list-bullet">${bullet}</div>
            <div class="ai-list-content">${content}</div>
          </div>`
        );
      } else if (isEmpty) {
        if (inList) { inList = false; } // End list on empty line
        // Only add spacer if previous output wasn't already a spacer
        const lastOutput = output[output.length - 1];
        if (lastOutput && !lastOutput.includes('class="h-2"')) {
          output.push('<div class="h-2"></div>'); // Spacer
        }
      } else {
        // Regular paragraph text
        if (inList) { inList = false; }

        // Wrap in p tag if not already wrapped (e.g. by our custom divs)
        if (!trimmed.startsWith('<div')) {
          // Check for multiple sentences to auto-format as pointers
          const rawParts = trimmed.split(/(?<=[.!?])\s+/);
          const sentences = [];

          if (rawParts.length > 0) {
            let currentSentence = rawParts[0];
            for (let j = 1; j < rawParts.length; j++) {
              const part = rawParts[j];
              if (part && part[0] && part[0] === part[0].toLowerCase() && part[0] !== part[0].toUpperCase()) {
                currentSentence += ' ' + part;
              } else {
                sentences.push(currentSentence);
                currentSentence = part;
              }
            }
            sentences.push(currentSentence);
          }

          if (sentences.length > 1) {
            sentences.forEach(s => {
              output.push(
                `<div class="ai-list-item">
                   <div class="ai-list-bullet">•</div>
                   <div class="ai-list-content">${s}</div>
                  </div>`
              );
            });
          } else {
            output.push(`<p class="mb-2 text-slate-700 leading-relaxed">${trimmed}</p>`);
          }
        } else {
          output.push(trimmed);
        }
      }
    });

    return output.join('');
  };

  return (
    <div className="space-y-4">
      {content.map((block, index) => {
        if (block.type === 'text') {
          return (
            <div
              key={index}
              className="ai-insight-container"
              dangerouslySetInnerHTML={{ __html: processTextContent(block.content) }}
            />
          );
        } else if (block.type === 'claim-proof') {
          // Check if proof is a table reference
          const tableMatch = typeof block.proof === 'string' ? block.proof.match(TABLE_PROOF_RE) : null;
          const isTableProof = !!tableMatch;
          const tableId = tableMatch?.[1];
          const tableTitle = tableMatch?.[2] || 'View Evidence';

          const isExpanded = expandedProofs.has(index);

          // Determine icon based on claim type or content
          let iconEmoji = '💡';

          const claimLower = block.claim.toLowerCase();
          if (claimLower.includes('critical') || claimLower.includes('missed') || claimLower.includes('drop')) {
            iconEmoji = '⚠️';
          } else if (claimLower.includes('trend') || claimLower.includes('inconsistent') || claimLower.includes('warning')) {
            iconEmoji = '📈';
          } else if (claimLower.includes('strength') || claimLower.includes('good') || claimLower.includes('perfect') || claimLower.includes('cleared')) {
            iconEmoji = '✅';
          } else if (claimLower.includes('verdict')) {
            iconEmoji = '📝';
          }

          // Extract finding number from claimNumber (Q1 -> 1, T1 -> 1)
          const findingNumber = block.claimNumber ? block.claimNumber.substring(1) : null;

          return (
            <div key={index} className="ai-insight-container">
              {/* Simple List Item Structure for Claim */}
              <div className="ai-list-item">
                <div className="ai-list-bullet">{iconEmoji}</div>
                <div className="ai-list-content">
                  {findingNumber && (
                    <span className="font-bold text-slate-900 mr-2">
                      Finding {findingNumber}:
                    </span>
                  )}

                  {/* Split claim into sentences and show as bullet points */}
                  {(() => {
                    let claimText = block.claim;

                    // Split by sentences (period, exclamation, question mark)
                    const sentences = claimText
                      .split(/(?<=[.!?])\s+/)
                      .map(s => s.trim())
                      .filter(s => s.length > 0);

                    if (sentences.length > 1) {
                      // Multiple sentences - show as bullet points
                      return (
                        <div className="space-y-1 mt-1">
                          {sentences.map((sentence, idx) => (
                            <div key={idx} className="flex gap-2">
                              <span className="text-slate-400">•</span>
                              <span className="text-slate-900">{sentence}</span>
                            </div>
                          ))}
                        </div>
                      );
                    } else {
                      // Single sentence - show as regular text
                      return (
                        <span className="font-medium text-slate-900">
                          {claimText}
                        </span>
                      );
                    }
                  })()}

                  {/* Inline Action Links */}
                  <div className="flex gap-3 mt-1">
                    {isTableProof ? (
                      <button
                        type="button"
                        onClick={() => onOpenEvidence?.(tableId, tableTitle)}
                        className="ai-evidence-link"
                      >
                        <BarChart3 className="w-3.5 h-3.5" />
                        View Evidence
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggleProof(index)}
                        className="ai-evidence-link"
                      >
                        {isExpanded ? (
                          <>
                            Hide Details <ChevronDown className="w-3.5 h-3.5" />
                          </>
                        ) : (
                          <>
                            View Details <ChevronRight className="w-3.5 h-3.5" />
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Expandable Proof Body - Simple Text Block */}
                  {!isTableProof && isExpanded && (
                    <div className="mt-3 pl-2 border-l-2 border-slate-200 text-sm text-slate-600 animate-fade-in">
                      <div className="whitespace-pre-wrap font-mono text-xs bg-slate-50 p-2 rounded">
                        {block.proof}
                      </div>
                      {block.reasoning && (
                        <p className="mt-2 text-slate-700 italic">
                          {block.reasoning}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

// Parse content to identify **Claim:** and **Proof:** patterns
// Enhanced to detect FINDING format and extract claim type/title
const parseContentWithProofs = (text) => {
  if (!text) return [];

  const blocks = [];
  const lines = text.split('\n');
  let currentClaim = null;
  let currentClaimType = null;
  let currentClaimNumber = null;
  let currentText = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Check if this line is a claim (supports **Claim:** or **Claim Q1:** format)
    const claimMatch = line.trim().match(/^\*\*Claim( ([QT]\d+))?:\*\*(.*)$/);
    if (claimMatch) {
      // Save any accumulated text
      if (currentText.trim()) {
        blocks.push({ type: 'text', content: currentText });
        currentText = '';
      }

      // Extract claim details
      currentClaimNumber = claimMatch[2]; // e.g., "Q1" or "T1"
      currentClaim = claimMatch[3].trim();
      currentClaimType = currentClaimNumber ? currentClaimNumber[0] : null; // 'Q' or 'T'
    }
    // Check if this line is proof
    else if (line.trim().startsWith('**Proof:**') && currentClaim) {
      // Extract proof (remove **Proof:** prefix)
      const proof = line.replace(/\*\*Proof:\*\*\s*/, '').trim();

      // Add claim-proof block
      blocks.push({
        type: 'claim-proof',
        claim: currentClaim,
        claimNumber: currentClaimNumber,
        claimType: currentClaimType,
        proof: proof
      });

      currentClaim = null;
      currentClaimNumber = null;
      currentClaimType = null;
    }
    // Regular text
    else {
      // If we had a claim but no proof, treat it as regular text
      if (currentClaim) {
        currentText += '**Claim:** ' + currentClaim + '\n';
        currentClaim = null;
        currentClaimNumber = null;
        currentClaimType = null;
      }
      currentText += line + '\n';
    }
  }

  // Add any remaining text
  if (currentText.trim()) {
    blocks.push({ type: 'text', content: currentText });
  }

  return blocks;
};

// Accordion Section Component (Level 1)
const AccordionSection = ({ id, icon, title, subtitle, iconColor = 'indigo', badge, isOpen, onToggle, children }) => {
  const colorClasses = {
    indigo: 'bg-indigo-50 text-indigo-600',
    red: 'bg-red-50 text-red-600',
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600'
  };

  const badgeColors = {
    red: 'bg-red-100 text-red-700 border-red-200',
    green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200'
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow mb-4">
      {/* Accordion Header - Always Visible */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${colorClasses[iconColor]} flex items-center justify-center flex-shrink-0`}>
            {icon}
          </div>
          <div className="text-left">
            <h2 className="text-base font-bold text-slate-900">{title}</h2>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {badge && (
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${badgeColors[badge.color]}`}>
              {badge.text}
            </span>
          )}
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </button>

      {/* Accordion Content - Collapsible */}
      {isOpen && (
        <div className="px-6 pb-6 border-t border-slate-100 pt-6 animate-fade-in">
          {children}
        </div>
      )}
    </div>
  );
};

// Nested Accordion Section Component (Level 2 - for VARC/DILR/QA)
const NestedAccordionSection = ({ id, title, icon, isOpen, onToggle, children }) => {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      {/* Nested Accordion Header */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors bg-slate-50/50"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
            {icon}
          </div>
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400" />
        )}
      </button>

      {/* Nested Accordion Content */}
      {isOpen && (
        <div className="px-4 pb-4 pt-3 bg-white animate-fade-in">
          {children}
        </div>
      )}
    </div>
  );
};

// Modal Trigger Button
const ModalTrigger = ({ label, icon, onClick }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-lg text-sm font-medium transition-colors border border-slate-200"
  >
    {icon}
    <span>{label}</span>
  </button>
);

// Modal Component
const Modal = ({ children, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-auto max-w-[95vw] max-h-[90vh] overflow-hidden">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Detailed Analysis</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-4 sm:p-6 md:p-8 overflow-y-auto max-h-[calc(90vh-80px)]">
          {children}
        </div>
      </div>
    </div>
  );
};

// Modal Content with Integrated Existing Components
const ModalContent = ({ type, testData, detailedData, analyticsData, scorecardData, studentCategory, currentViewMode = 'BLACKI' }) => {
  if (!detailedData && !analyticsData && !scorecardData) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">Loading detailed data...</p>
      </div>
    );
  }

  // Evidence Table Modal - NEW
  if (type?.type === 'evidence') {
    // Determine which evidence to use
    let tierEvidence;
    
    if (type.useGeneric) {
      // Use generic evidence (backward compatibility)
      tierEvidence = detailedData?.evidenceGeneric || {};
    } else {
      // Try tier-specific evidence first
      tierEvidence = detailedData?.evidence?.[currentViewMode];
      
      // Fallback: if tier evidence doesn't exist, try other structures
      if (!tierEvidence || Object.keys(tierEvidence).length === 0) {
        if (detailedData?.evidence?.BLACKI || detailedData?.evidence?.NON_BLACKI) {
          tierEvidence = detailedData.evidence[currentViewMode] || detailedData.evidence.BLACKI || {};
        } else {
          tierEvidence = detailedData?.evidence || {};
        }
      }
    }
    
    // Check for nested structure (e.g., question_type_breakdown.varc)
    let tbl = tierEvidence[type.tableId];
    
    // If not found directly, check in nested structures
    if (!tbl && type.nestedKey && tierEvidence[type.nestedKey]) {
      tbl = tierEvidence[type.nestedKey][type.tableId];
    }
    
    // If still not found, try to find in any nested structure
    if (!tbl) {
      const nestedKeys = ['question_type_breakdown', 'area_performance', 'time_analysis'];
      for (const nestedKey of nestedKeys) {
        if (tierEvidence[nestedKey]) {
          const sectionKey = type.tableId.toLowerCase();
          if (tierEvidence[nestedKey][sectionKey]) {
            tbl = tierEvidence[nestedKey][sectionKey];
            break;
          }
        }
      }
    }

    if (!tbl) {
      return (
        <div className="text-center py-12">
          <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Evidence data not found.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {(type.title || tbl.title) && (
          <div>
            <h3 className="text-xl font-bold text-slate-900">
              {type.title || tbl.title}
            </h3>
            {tbl.subtitle && (
              <p className="text-sm text-slate-600 mt-1">{tbl.subtitle}</p>
            )}
          </div>
        )}

        {/* DESKTOP: Full Table */}
        <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {tbl.columns.map((col, i) => (
                  <th key={i} className="text-left px-3 py-2 font-semibold text-ims-text-primary border-b border-gray-200">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tbl.rows.map((row, ri) => {
                // Check if this is a score/cutoff row (empty test name and date)
                const isScoreRow = row[0] === '' && row[1] === '';

                // Highlight first row if flagged (current test) - but only for status rows, not score rows
                const isHighlighted = tbl.highlightFirstRow && ri === 0;

                let rowClassName;
                if (isScoreRow) {
                  // Score rows: subtle background, no bottom border, smaller text
                  rowClassName = 'bg-gray-50/30';
                } else if (isHighlighted) {
                  // Highlighted status row (current test)
                  rowClassName = 'bg-blue-50 border-l-4 border-ims-primary';
                } else {
                  // Regular status rows
                  rowClassName = (ri % 4 >= 2) ? 'bg-gray-50/60' : 'bg-white'; // Pair rows (status + score)
                }

                return (
                  <tr key={ri} className={rowClassName}>
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className={`px-3 py-2 whitespace-nowrap ${isScoreRow
                          ? 'border-b border-gray-100 text-ims-text-secondary text-xs italic' // Score row styling
                          : isHighlighted
                            ? 'text-ims-text-primary font-semibold border-b-0' // Highlighted status row
                            : 'text-ims-text-primary border-b-0' // Regular status row
                          }`}
                      >
                        {String(cell ?? '')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* MOBILE: Card-based layout */}
        <div className="md:hidden space-y-3">
          {tbl.rows.filter((row, ri) => !(row[0] === '' && row[1] === '')).map((row, ri) => {
            // Find the corresponding score row (next row if it exists)
            const scoreRow = tbl.rows[ri * 2 + 1];
            const isHighlighted = tbl.highlightFirstRow && ri === 0;

            return (
              <div
                key={ri}
                className={`border border-gray-200 rounded-xl overflow-hidden ${
                  isHighlighted ? 'bg-blue-50 border-l-4 border-l-ims-primary' : 'bg-white'
                }`}
              >
                {/* Card Header - First two columns (Test, Date) */}
                <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-ims-text-primary">{row[0]}</p>
                    <p className="text-xs text-ims-text-secondary">{row[1]}</p>
                  </div>
                  {isHighlighted && (
                    <span className="text-xs bg-ims-primary text-white px-2 py-1 rounded-full">Current</span>
                  )}
                </div>

                {/* Card Body - Remaining columns */}
                <div className="px-4 py-3 space-y-2">
                  {tbl.columns.slice(2).map((col, ci) => (
                    <div key={ci} className="flex justify-between items-center">
                      <span className="text-sm text-ims-text-secondary">{col}</span>
                      <span className={`text-sm font-medium ${
                        String(row[ci + 2] || '').includes('CLEARED')
                          ? 'text-ims-success'
                          : String(row[ci + 2] || '').includes('MISSED')
                            ? 'text-ims-error'
                            : 'text-ims-text-primary'
                      }`}>
                        {row[ci + 2] || '-'}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Score row (if exists) */}
                {scoreRow && scoreRow[0] === '' && scoreRow[1] === '' && (
                  <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                    <div className="grid grid-cols-4 gap-2 text-center">
                      {tbl.columns.slice(2).map((col, ci) => (
                        <div key={ci}>
                          <p className="text-xs text-ims-text-muted">{col}</p>
                          <p className="text-xs text-ims-text-secondary italic">{scoreRow[ci + 2] || '-'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {tbl.sourceHint && (
          <p className="text-xs text-slate-500 italic">Source: {tbl.sourceHint}</p>
        )}
      </div>
    );
  }

  // Scorecard Modal - Use full ScoreCard component
  if (type === 'scorecard') {
    if (analyticsData && detailedData) {
      return (
        <div className="space-y-4">
          <ScoreCard
            questionsData={analyticsData.questionsData}
            students={analyticsData.students}
            tests={analyticsData.tests}
            selectedStudent={analyticsData.selectedStudent}
            selectedTest={analyticsData.selectedTest}
            percentileData={analyticsData.percentileData}
            studentCategory={studentCategory}
            categoryCutoffs={analyticsData.cutoffs}
          />
        </div>
      );
    }
  }

  // Section Breakdown Modal (placeholder)
  const sectionMatch = type.match(/(varc|dilr|qa)-breakdown/);
  if (sectionMatch) {
    const sectionName = sectionMatch[1].toUpperCase();
    return (
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-slate-900 mb-4">{sectionName} Accuracy Analysis</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900">
            <strong>Coming Soon:</strong> Detailed accuracy breakdown for {sectionName}.
          </p>
        </div>
      </div>
    );
  }

  // Type A Analysis Modal (placeholder)
  const typeAMatch = type.match(/(varc|dilr|qa)-typeA/);
  if (typeAMatch) {
    const sectionName = typeAMatch[1].toUpperCase();
    return (
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-slate-900 mb-4">{sectionName} Speed Analysis (Type A Focus)</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900">
            <strong>Coming Soon:</strong> Speed analysis focusing on Type A questions for {sectionName}.
          </p>
        </div>
      </div>
    );
  }

  // Historical Trend (placeholder for now)
  if (type === 'historical') {
    return (
      <div className="space-y-6">
        <h3 className="text-xl font-bold text-slate-900">Historical Performance Trend</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900">
            <strong>Coming Soon:</strong> Track your performance across multiple tests to see improvement trends.
          </p>
        </div>
      </div>
    );
  }

  // Default fallback
  return (
    <div className="text-center py-12">
      <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
      <h4 className="text-lg font-semibold text-slate-900 mb-2">
        {type.replace('-', ' ').toUpperCase()}
      </h4>
      <p className="text-slate-500">Detailed breakdown coming soon...</p>
    </div>
  );
};



export default StatGenieReportCard;
